import { Menu, MessageSquare, LogOut, User, Minimize2, Maximize2, Sun, Moon, Monitor, Activity } from 'lucide-react'
import { NotificationBell } from '@/components/NotificationBell'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/stores/authStore'
import { useUiStore } from '@/stores/uiStore'
import { useTimerStore, formatTimerDisplay } from '@/stores/timerStore'
import { useChatStore } from '@/stores/chatStore'
import type { Theme } from '@/stores/uiStore'

const themeIcons: Record<Theme, React.ElementType> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
}

const themeLabels: Record<Theme, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
}

const themeOrder: Theme[] = ['light', 'dark', 'system']

export function Header() {
  const { user, logout } = useAuthStore()
  const { toggleSidebar, toggleChat, focusMode, toggleFocusMode, theme, setTheme, toggleActivityPanel } = useUiStore()
  const { isRunning, taskTitle, elapsedSeconds } = useTimerStore()
  const isAgentActive = useChatStore((s) => s.isLoading)

  const initials = user?.name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  const cycleTheme = () => {
    const currentIndex = themeOrder.indexOf(theme)
    const nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % themeOrder.length
    setTheme(themeOrder[nextIndex]!)
  }

  const ThemeIcon = themeIcons[theme]

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-14 items-center px-4 gap-4">
        {/* Menu toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="md:hidden"
        >
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>

        {/* Logo + Agent status dot */}
        <div className="flex items-center gap-2">
          <span className="font-mono text-lg font-semibold bg-gradient-to-r from-primary to-[hsl(280,100%,70%)] bg-clip-text text-transparent">
            CA
          </span>
          <span className="font-semibold text-foreground/90">Copilot</span>
          {/* Agent presence indicator */}
          <div
            className={
              isAgentActive
                ? 'w-2 h-2 rounded-full bg-primary animate-glow-pulse'
                : 'w-2 h-2 rounded-full bg-primary/40'
            }
            title={isAgentActive ? 'Agent is working' : 'Agent ready'}
            aria-label={isAgentActive ? 'Agent is working' : 'Agent ready'}
          />
        </div>

        {/* Timer display (when running) */}
        {isRunning && (
          <div className="hidden sm:flex items-center gap-2 px-3 py-1 glass-surface rounded-lg">
            <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span className="font-mono text-sm text-primary">
              {formatTimerDisplay(elapsedSeconds)}
            </span>
            <span className="text-xs text-muted-foreground truncate max-w-32">
              {taskTitle}
            </span>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Notifications */}
        <NotificationBell />

        {/* Activity feed toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleActivityPanel}
          title="Agent Activity"
          aria-label="Agent Activity"
          className="h-9 w-9"
        >
          <Activity className="h-4 w-4" />
        </Button>

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={cycleTheme}
          title={`Theme: ${themeLabels[theme]}`}
          aria-label={`Theme: ${themeLabels[theme]}`}
          className="h-9 w-9"
        >
          <ThemeIcon className="h-4 w-4" />
        </Button>

        {/* Focus Mode toggle */}
        <Button
          variant={focusMode ? 'default' : 'ghost'}
          size="icon"
          onClick={toggleFocusMode}
          title={focusMode ? 'Full Dashboard' : 'Focus Mode'}
          aria-label={focusMode ? 'Full Dashboard' : 'Focus Mode'}
          className="h-9 w-9"
        >
          {focusMode ? (
            <Maximize2 className="h-4 w-4" />
          ) : (
            <Minimize2 className="h-4 w-4" />
          )}
        </Button>

        {/* Chat toggle */}
        <Button
          variant="outline"
          size="sm"
          onClick={toggleChat}
          className="gap-2 rounded-full border-primary/30 hover:border-primary/60 bg-gradient-to-r from-primary/10 to-transparent"
        >
          <MessageSquare className="h-4 w-4" />
          <span className="hidden sm:inline">Ask Copilot</span>
        </Button>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary/10 text-primary">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user?.name}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <User className="mr-2 h-4 w-4" />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-destructive">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
